import React from "react";
import { motion } from "framer-motion";
import { FaUsers, FaTools, FaHandshake, FaStar } from "react-icons/fa";

// --- Theme Colors (Defined Locally for Component Safety) ---
// Note: In a real app, use Tailwind config variables or CSS variables here.
const ACCENT_BLUE = "#3498DB";
const ACCENT_BLUE_DARK = "#1A5276";
const DARK_CARD_BG = "#151515"; // Near black for cards
const VERY_DARK_BG = "#0b0f0d"; // Section background

const AboutSection = () => {
  // Animation variants for staggered stats
  const statsVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, delay: i * 0.2, ease: "easeOut" },
    }),
  };

  // --- Tailwind Class Mapping ---
  // bg-gray-900 -> bg-[#0b0f0d] (Section background)
  // text-green-500 -> text-[#3498DB] (Accent color)
  // bg-gradient-to-r from-green-500 to-teal-400 -> from-[#3498DB] to-[#1A5276] (Blue gradient)
  // from-gray-800 to-gray-700 -> from-[#151515] to-[#0b0f0d] (Card gradient)

  return (
    <section className={`bg-[${VERY_DARK_BG}] text-white py-20 px-6 md:px-8 lg:px-12 max-w-7xl mx-auto relative`}>
      {/* Section Title */}
      <motion.div
        className={`text-[${ACCENT_BLUE}] text-xl font-semibold mb-4 tracking-wide`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        WHO WE ARE:
      </motion.div>

      {/* Main Heading with Gradient Underline */}
      <motion.h2
        className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-8 leading-tight relative"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
      >
        Accelerating Success, Innovation, and Partnership 🌐
        <span className={`absolute bottom-0 left-0 w-24 h-1 bg-gradient-to-r from-[${ACCENT_BLUE}] to-[${ACCENT_BLUE_DARK}]`} />
      </motion.h2>

      {/* Description */}
      <motion.p
        className="text-lg md:text-xl text-gray-200 mb-12 max-w-3xl leading-7 tracking-wide"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        A company that positions itself as a key player in the development of decentralized applications (dApps), focusing on building secure, scalable, and verifiable solutions on various blockchain platforms like Ethereum, Solana, and emerging Layer-2 networks. They emphasize a commitment to practical utility and "code as the ultimate truth," suggesting a strong focus on technical rigor and transparency in their development process.
      </motion.p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
        {/* Stat Item 1 */}
        <motion.div
          className={`p-8 bg-gradient-to-br from-[${DARK_CARD_BG}] to-[${VERY_DARK_BG}] rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 space-y-2`}
          custom={0}
          initial="hidden"
          whileInView="visible"
          variants={statsVariants}
          viewport={{ once: true }}
        >
          <div className="flex items-center mb-3">
            <FaUsers className={`text-[${ACCENT_BLUE}] text-4xl mr-3`} />
            <div className="text-3xl font-bold">
              <span className={`text-[${ACCENT_BLUE}]`}>+</span>850
            </div>
          </div>
          <div className="text-base text-gray-200 font-semibold">
            Active Community Members
          </div>
          <div className="text-sm text-gray-400 leading-relaxed">
            Engaging a vibrant network of tech enthusiasts.
          </div>
        </motion.div>

        {/* Stat Item 2 */}
        <motion.div
          className={`p-8 bg-gradient-to-br from-[${DARK_CARD_BG}] to-[${VERY_DARK_BG}] rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 space-y-2`}
          custom={1}
          initial="hidden"
          whileInView="visible"
          variants={statsVariants}
          viewport={{ once: true }}
        >
          <div className="flex items-center mb-3">
            <FaTools className={`text-[${ACCENT_BLUE}] text-4xl mr-3`} />
            <div className="text-3xl font-bold">
              <span className={`text-[${ACCENT_BLUE}]`}>+</span>750
            </div>
          </div>
          <div className="text-base text-gray-200 font-semibold">
            Tech Projects Delivered
          </div>
          <div className="text-sm text-gray-400 leading-relaxed">
            Crafting innovative solutions for diverse industries.
          </div>
        </motion.div>

        {/* Stat Item 3 */}
        <motion.div
          className={`p-8 bg-gradient-to-br from-[${DARK_CARD_BG}] to-[${VERY_DARK_BG}] rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 space-y-2`}
          custom={2}
          initial="hidden"
          whileInView="visible"
          variants={statsVariants}
          viewport={{ once: true }}
        >
          <div className="flex items-center mb-3">
            <FaHandshake className={`text-[${ACCENT_BLUE}] text-4xl mr-3`} />
            <div className="text-3xl font-bold">
              <span className={`text-[${ACCENT_BLUE}]`}>+</span>300
            </div>
          </div>
          <div className="text-base text-gray-200 font-semibold">
            Business Partnerships
          </div>
          <div className="text-sm text-gray-400 leading-relaxed">
            Fostering collaborations with leading businesses.
          </div>
        </motion.div>

        {/* Stat Item 4 */}
        <motion.div
          className={`p-8 bg-gradient-to-br from-[${DARK_CARD_BG}] to-[${VERY_DARK_BG}] rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 space-y-2`}
          custom={3}
          initial="hidden"
          whileInView="visible"
          variants={statsVariants}
          viewport={{ once: true }}
        >
          <div className="flex items-center mb-3">
            <FaStar className={`text-[${ACCENT_BLUE}] text-4xl mr-3`} />
            <div className="text-3xl font-bold">
              <span className={`text-[${ACCENT_BLUE}]`}>+</span>500
            </div>
          </div>
          <div className="text-base text-gray-200 font-semibold">
            Careers Enhanced
          </div>
          <div className="text-sm text-gray-400 leading-relaxed">
            Empowering professionals with skills and opportunities.
          </div>
        </motion.div>
      </div>

      {/* CTA Button */}
      <motion.button
        className={`border-2 border-[${ACCENT_BLUE}] text-[${ACCENT_BLUE}] px-10 py-4 text-lg font-semibold rounded-lg bg-[${VERY_DARK_BG}] hover:bg-gradient-to-r hover:from-[${ACCENT_BLUE}] hover:to-[${ACCENT_BLUE_DARK}] hover:text-white hover:border-transparent transition-all duration-300`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        viewport={{ once: true }}
        whileHover={{
          scale: 1.05,
          boxShadow: `0 0 15px ${ACCENT_BLUE}80`, // Apply shadow using ACCENT_BLUE
        }}
        whileTap={{ scale: 0.95 }}
      >
        Discover Our Story 
      </motion.button>
    </section>
  );
};

export default AboutSection;
